import { useMemo, useState } from "react";
import { ChevronDown, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { PublicReviewsSummary } from "@/lib/reviews";
import { Stars } from "./stars";
import { formatarNota, formatarMesAno } from "./format";

const NOTAS = [5, 4, 3, 2, 1] as const;

interface ReviewsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: PublicReviewsSummary;
}

/**
 * O modal de avaliações: média grande à esquerda, distribuição por nota à
 * direita, lista de comentários abaixo — a mesma leitura do painel do Google.
 *
 * O filtro por nota existe porque a primeira coisa que um comprador cético faz
 * é procurar as notas baixas. Esconder isso é o que faz um painel de avaliação
 * parecer maquiado; deixar o clique de "1 estrela" funcionar é o que faz o
 * 5 estrelas ser levado a sério.
 *
 * SOBRE O MOBILE: num celular de 660px de altura, cabeçalho + resumo + rodapé
 * fixos consumiam quase tudo e sobrava cerca de uma linha para os comentários
 * — justamente o conteúdo que a pessoa abriu o modal para ler. Por isso, abaixo
 * de `sm`: a descrição do cabeçalho vira `sr-only`, as cinco barras de
 * distribuição entram atrás de um toggle, o rodapé perde a segunda frase e os
 * espaçamentos encolhem. No desktop nada disso se aplica — lá sobra altura.
 */
export function ReviewsDialog({
  open,
  onOpenChange,
  data,
}: ReviewsDialogProps) {
  const [filtro, setFiltro] = useState<number | null>(null);
  // Só governa o mobile: a partir de `sm` a distribuição é sempre visível.
  const [mostrarDistribuicao, setMostrarDistribuicao] = useState(false);

  const itens = useMemo(
    () =>
      filtro === null
        ? data.items
        : data.items.filter((r) => r.rating === filtro),
    [data.items, filtro],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `dvh` e não `vh`: no celular o `vh` mede a viewport como se a barra de
          endereço estivesse escondida, então `max-h-[90vh]` estoura a área
          realmente visível. `gap-0` desliga o `gap-4` padrão do DialogContent,
          que somava 48px de vão morto entre as quatro seções. */}
      <DialogContent className="gap-0 max-h-[90dvh] sm:max-h-[85vh] sm:max-w-2xl">
        <DialogHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl sm:text-2xl font-semibold tracking-tight">
            Avaliações do Ecclesiato
          </DialogTitle>
          {/* `sr-only` no mobile em vez de removida: o Radix usa a descrição no
              `aria-describedby`, então ela precisa continuar no DOM. */}
          <DialogDescription className="sr-only sm:not-sr-only">
            Notas e comentários enviados por líderes que usam a plataforma no
            dia a dia da igreja.
          </DialogDescription>
        </DialogHeader>

        {/* ── Resumo: média + distribuição ──────────────────────────── */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-border shrink-0 flex flex-col sm:flex-row gap-3 sm:gap-8 sm:items-center">
          <div className="flex sm:flex-col items-center gap-4 sm:gap-1 shrink-0">
            <span className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none text-foreground">
              {formatarNota(data.average)}
            </span>
            <div className="flex flex-col sm:items-center gap-1">
              <Stars value={data.average} size="md" />
              <span className="text-xs text-muted-foreground">
                {data.total} {data.total === 1 ? "avaliação" : "avaliações"}
              </span>
            </div>

            {/* Toggle das barras — só existe no mobile. */}
            <button
              type="button"
              onClick={() => setMostrarDistribuicao((v) => !v)}
              aria-expanded={mostrarDistribuicao}
              className="sm:hidden ml-auto flex items-center gap-1 text-xs font-semibold text-primary"
            >
              {mostrarDistribuicao ? "Ocultar notas" : "Ver notas"}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  mostrarDistribuicao && "rotate-180",
                )}
              />
            </button>
          </div>

          <div
            className={cn(
              "flex-1 space-y-1.5 min-w-0 sm:block",
              mostrarDistribuicao ? "block" : "hidden",
            )}
          >
            {NOTAS.map((nota) => {
              const qtd = data.distribution[nota] || 0;
              const pct = data.total > 0 ? (qtd / data.total) * 100 : 0;
              const ativo = filtro === nota;
              return (
                <button
                  key={nota}
                  type="button"
                  disabled={qtd === 0}
                  onClick={() => setFiltro(ativo ? null : nota)}
                  aria-pressed={ativo}
                  className={cn(
                    "group w-full flex items-center gap-2.5 rounded-md px-1.5 py-0.5 -mx-1.5 text-left transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    qtd === 0
                      ? "cursor-default opacity-50"
                      : "hover:bg-secondary/60",
                    ativo && "bg-secondary",
                  )}
                >
                  <span className="flex items-center gap-0.5 text-xs text-muted-foreground w-7 shrink-0 tabular-nums">
                    {nota}
                    <Star className="w-3 h-3 text-lp-rating fill-lp-rating" />
                  </span>
                  <span className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <span
                      className={cn(
                        "block h-full rounded-full transition-all duration-500",
                        ativo ? "bg-primary" : "bg-lp-rating",
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-xs text-muted-foreground w-6 text-right shrink-0 tabular-nums">
                    {qtd}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Lista de comentários ──────────────────────────────────── */}
        <div className="overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 flex-1 min-h-0">
          {filtro !== null && (
            <div className="flex items-center justify-between mb-4 text-xs">
              <span className="text-muted-foreground">
                Mostrando só as de{" "}
                <strong className="text-foreground font-semibold">
                  {filtro} {filtro === 1 ? "estrela" : "estrelas"}
                </strong>
              </span>
              <button
                type="button"
                onClick={() => setFiltro(null)}
                className="font-semibold text-primary hover:underline"
              >
                Ver todas
              </button>
            </div>
          )}

          {itens.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Nenhum comentário nessa nota.
            </p>
          ) : (
            <ul className="space-y-5">
              {itens.map((r) => (
                <li
                  key={r.id}
                  className="pb-5 border-b border-border/60 last:border-0 last:pb-0"
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">
                        {r.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm text-foreground">
                        {r.authorName}
                      </span>
                      {/* Linha própria e sem `truncate`: no celular, função +
                          nome da igreja não cabem ao lado do nome, e o
                          `truncate` cortava a igreja em "IEQ Central Pir…" —
                          some justamente a informação que dá credibilidade. */}
                      {r.churchName && (
                        <span className="block text-xs text-muted-foreground leading-snug">
                          {r.role ? `${r.role} · ` : ""}
                          {r.churchName}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mt-1.5 mb-2">
                        <Stars value={r.rating} size="xs" />
                        <span className="text-[11px] text-muted-foreground">
                          {formatarMesAno(r.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
                        {r.comment}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 sm:py-3.5 border-t border-border shrink-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <p className="text-[11px] text-muted-foreground leading-relaxed max-w-md">
            Só quem tem conta ativa no Ecclesiato pode avaliar, uma avaliação
            por conta.
            {/* A segunda frase é contexto, não regra — no celular ela sozinha
                custava mais uma linha de altura do rodapé. */}
            <span className="hidden sm:inline">
              {" "}
              Publicamos elogio e crítica — a média acima considera todas as
              avaliações aprovadas.
            </span>
          </p>
          <a
            href="/avaliar"
            className="text-[11px] font-semibold text-primary hover:underline shrink-0"
          >
            Sou cliente, quero avaliar
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
